<template>
  <div class="article-preview-wrapper">
    <div class="preview-content">
      <!-- ========== 1페이지: 커버 ========== -->
      <div class="paper-page" :style="getPageStyle()">
        <div class="page-inner" :style="getPageInnerStyle(getGlobalPageNumber(1))">
          <div class="cover-title-area">
            <h1 class="cover-main-title">
              {{ item?.title }}<span v-if="item?.subtitle">: {{ item.subtitle }}</span
              ><span v-if="hasTitlePageComment">*</span>
            </h1>
            <h2 v-if="item?.title_eng" class="cover-sub-title">
              {{ item.title_eng }}
            </h2>
          </div>
          <div class="cover-divider"></div>
          <div class="cover-authors-area">
            <div
              v-for="(author, idx) in item?.authors || []"
              :key="idx"
              class="author-item"
            >
              <span class="author-name">{{ author.full_name }}</span>
              <span v-if="author.name_eng" class="author-name-eng">{{ author.name_eng }}</span>
              <span v-if="author.organization" class="author-org">{{ author.organization }}</span>
              <span v-if="author.organization_eng" class="author-org-eng">{{ author.organization_eng }}</span>
            </div>
          </div>
        </div>
        <div
          v-if="coverComments.length > 0"
          class="page-comments-bottom"
          :style="getCommentsStyle(getGlobalPageNumber(1))"
        >
          <div class="comments-divider"></div>
          <div
            v-for="comment in coverComments"
            :key="comment.idx"
            class="page-comment"
          >
            <sup>{{ comment.superscript }}</sup> {{ comment.content }}
          </div>
        </div>
      </div>

      <!-- ========== 2페이지~: 초록 ========== -->
      <div
        v-for="(abstractPage, index) in abstractPages"
        :key="`abstract-${index}`"
        class="paper-page"
        :style="getPageStyle()"
      >
        <div class="page-inner" :style="getPageInnerStyle(getGlobalPageNumber(index + 2))">
          <h3 v-if="index === 0" class="abstract-title">● 초록(Abstract)</h3>
          <div class="abstract-content" v-html="abstractPage"></div>
          <div
            v-if="index === abstractPages.length - 1 && (item?.keyword || item?.keyword_eng)"
            class="keyword-area"
          >
            <span class="keyword-label">핵심어</span>
            <span class="keyword-text">{{ item?.keyword || item?.keyword_eng }}</span>
          </div>
        </div>
        <div
          v-if="getGlobalPageNumber(index + 2) % 2 !== 0"
          class="page-footer page-footer-odd"
          :style="getFooterStyle(getGlobalPageNumber(index + 2))"
        >
          <span class="page-number">{{ getGlobalPageNumber(index + 2) }}</span>
          <span class="footer-text">{{ footerGroupTitle }}</span>
        </div>
        <div
          v-else
          class="page-footer page-footer-even"
          :style="getFooterStyle(getGlobalPageNumber(index + 2))"
        >
          <span class="footer-text">{{ item?.title || "" }}</span>
          <span class="page-number">{{ getGlobalPageNumber(index + 2) }}</span>
        </div>
      </div>

      <!-- ========== 3페이지~: 본문 ========== -->
      <div
        v-for="(pageContent, index) in contentPages"
        :key="`content-${index}`"
        class="paper-page"
        :style="getPageStyle()"
      >
        <div
          class="page-inner"
          :style="getPageInnerStyle(getGlobalPageNumber(index + contentStartPage))"
        >
          <div class="paper-content" v-html="pageContent"></div>
        </div>
        <div
          v-if="splitComments[index] && splitComments[index].length > 0"
          class="page-comments-bottom"
          :style="getCommentsStyle(getGlobalPageNumber(index + contentStartPage))"
        >
          <div class="comments-divider"></div>
          <div
            v-for="comment in splitComments[index]"
            :key="comment.idx"
            class="page-comment"
          >
            <sup>{{ comment.superscript }}</sup> {{ comment.content }}
          </div>
        </div>
        <div
          v-if="getGlobalPageNumber(index + contentStartPage) % 2 !== 0"
          class="page-footer page-footer-odd"
          :style="getFooterStyle(getGlobalPageNumber(index + contentStartPage))"
        >
          <span class="page-number">{{ getGlobalPageNumber(index + contentStartPage) }}</span>
          <span class="footer-text">{{ footerGroupTitle }}</span>
        </div>
        <div
          v-else
          class="page-footer page-footer-even"
          :style="getFooterStyle(getGlobalPageNumber(index + contentStartPage))"
        >
          <span class="footer-text">{{ item?.title || "" }}</span>
          <span class="page-number">{{ getGlobalPageNumber(index + contentStartPage) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  PAGE_DIMENSIONS,
  getContentWidth,
  getPagePadding,
  splitContentByHeight,
} from "@/utils/paperEditor";
import { parseContentToAst, renderAstToHtml } from "@/utils/paperAst";
import { sanitizeHtml } from "@/utils/sanitizeHtml";

const SERVER_URL = process.env.VUE_APP_SERVER_URI;

export default {
  name: "ArticlePreviewTemplate",
  props: {
    item: {
      type: Object,
      required: true,
    },
    journal: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    group: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    pageSize: {
      type: String,
      required: false,
      default: "A5",
    },
    startPageNumber: {
      type: Number,
      required: false,
      default: 1,
    },
    previewScale: {
      type: Number,
      required: false,
      default: 1,
    },
  },
  computed: {
    pageSizeKey() {
      return (this.pageSize || "A5").toUpperCase();
    },
    dimensions() {
      return PAGE_DIMENSIONS[this.pageSizeKey] || PAGE_DIMENSIONS.A5;
    },
    abstractSource() {
      if (!this.item) return "";
      return this.item.abstract || this.item.abstract_eng || "";
    },
    abstractHtml() {
      if (!this.abstractSource) return "";
      return this.processImageUrls(
        renderAstToHtml(parseContentToAst(this.abstractSource))
      );
    },
    abstractPages() {
      if (!this.abstractHtml) return [""];
      return splitContentByHeight(
        this.abstractHtml,
        this.pageSizeKey,
        this.getAbstractCoverHeight(),
        {
          containerStyle:
            'font-family: "KoPubWorldBatang", "Noto Serif KR", serif; font-size: 10pt; line-height: 1.8; text-align: justify;',
          paragraphStyle: "margin: 0 0 10px 0;",
        }
      );
    },
    contentStartPage() {
      return 2 + this.abstractPages.length;
    },
    htmlContent() {
      return this.processImageUrls(this.normalizeContentToHtml(this.item?.content));
    },
    contentPages() {
      if (!this.htmlContent) return [""];
      return splitContentByHeight(this.htmlContent, this.pageSizeKey, 0);
    },
    splitComments() {
      const comments = this.item?.comments || [];
      if (!comments.length || !this.contentPages.length) {
        return this.contentPages.map(() => []);
      }
      const result = this.contentPages.map(() => []);
      this.contentPages.forEach((pageContent, pageIndex) => {
        const markerRegex =
          /data-footnote-id="(\d+)"|<sup[^>]*>\^?(\d+)\)?<\/sup>/g;
        let match;
        while ((match = markerRegex.exec(pageContent)) !== null) {
          const footnoteId = match[1] || match[2];
          const comment = comments.find((c) => {
            const num = c.superscript?.toString().replace(/\D/g, "");
            return num === footnoteId;
          });
          if (comment && !result[pageIndex].includes(comment)) {
            result[pageIndex].push(comment);
          }
        }
      });
      return result;
    },
    coverComments() {
      return this.getCoverCommentsForPaper(this.item);
    },
    hasTitlePageComment() {
      return (this.item?.comments || []).some((comment) =>
        this.isTitlePageComment(comment)
      );
    },
    footerGroupTitle() {
      return (
        this.group?.title ||
        this.item?.group?.title ||
        this.journal?.title ||
        this.item?.journal?.title ||
        ""
      );
    },
  },
  methods: {
    getGlobalPageNumber(localPageNumber) {
      const base = Number(this.startPageNumber) || 1;
      return base + localPageNumber - 1;
    },
    getPageStyle() {
      const base = this.getPdfPageStyle();
      const scale = this.previewScale;
      return {
        ...base,
        width: `${this.dimensions.width * scale}px`,
        height: `${this.dimensions.height * scale}px`,
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        marginBottom: "16px",
        flexShrink: 0,
      };
    },
    getPageInnerStyle(pageNumber) {
      const base = this.getPdfPageInnerStyle(pageNumber);
      const scale = this.previewScale;
      return {
        ...base,
        transform: `scale(${scale})`,
        transformOrigin: "top left",
      };
    },
    getFooterStyle(pageNumber) {
      const base = this.getPdfFooterStyle(pageNumber);
      const scale = this.previewScale;
      return {
        left: `${parseFloat(base.left) * scale}px`,
        right: `${parseFloat(base.right) * scale}px`,
        bottom: `${parseFloat(base.bottom) * scale}px`,
      };
    },
    getCommentsStyle(pageNumber) {
      const base = this.getPdfCommentsStyle(pageNumber);
      const scale = this.previewScale;
      return {
        left: `${parseFloat(base.left) * scale}px`,
        right: `${parseFloat(base.right) * scale}px`,
        bottom: `${parseFloat(base.bottom) * scale}px`,
      };
    },
    getAbstractCoverHeight() {
      return this.getAbstractCoverHeightForPaper(this.item);
    },
    getAbstractCoverHeightForPaper(paper) {
      if (typeof window === "undefined" || typeof document === "undefined") {
        return 0;
      }

      const contentWidth = getContentWidth(this.pageSizeKey, true);
      const container = document.createElement("div");
      container.style.cssText = `
        position: absolute;
        visibility: hidden;
        width: ${contentWidth}px;
        padding: 0;
      `;

      const titleEl = document.createElement("div");
      titleEl.textContent = "● 초록(Abstract)";
      titleEl.style.cssText = `
        font-size: 12pt;
        font-weight: bold;
        margin: 0 0 16px 0;
      `;
      container.appendChild(titleEl);

      let keywordReserve = 0;
      if (paper?.keyword || paper?.keyword_eng) {
        const keywordWrap = document.createElement("div");
        keywordWrap.style.cssText = `
          font-size: 9pt;
          line-height: 1.4;
        `;
        const keywordLabel = document.createElement("span");
        keywordLabel.textContent = "핵심어";
        keywordLabel.style.cssText = `
          display: inline-block;
          padding: 2px 8px;
          font-size: 9pt;
        `;
        const keywordText = document.createElement("span");
        keywordText.textContent = paper.keyword || paper.keyword_eng || "";
        keywordText.style.cssText = "font-size: 9pt;";
        keywordWrap.appendChild(keywordLabel);
        keywordWrap.appendChild(keywordText);
        container.appendChild(keywordWrap);
      }

      document.body.appendChild(container);
      const titleStyles = window.getComputedStyle(titleEl);
      const titleHeight =
        titleEl.offsetHeight +
        (parseFloat(titleStyles.marginTop) || 0) +
        (parseFloat(titleStyles.marginBottom) || 0);

      if (paper?.keyword || paper?.keyword_eng) {
        const keywordWrap = container.lastChild;
        const keywordHeight = keywordWrap ? keywordWrap.offsetHeight : 0;
        const keywordBottom = 60;
        keywordReserve = keywordHeight + keywordBottom;
      }

      document.body.removeChild(container);
      return Math.ceil(titleHeight + keywordReserve);
    },
    getPdfPageStyle() {
      return {
        width: `${this.dimensions.width}px`,
        height: `${this.dimensions.height}px`,
        backgroundColor: "white",
        position: "relative",
        overflow: "hidden",
        pageBreakAfter: "always",
      };
    },
    getPdfPageInnerStyle(pageNumber) {
      const { paddingLeft, paddingRight } = getPagePadding(
        this.pageSizeKey,
        pageNumber
      );
      return {
        width: `${this.dimensions.width}px`,
        height: `${this.dimensions.height}px`,
        padding: `${this.dimensions.verticalPadding}px ${paddingRight}px ${this.dimensions.verticalPadding}px ${paddingLeft}px`,
        boxSizing: "border-box",
        fontFamily: '"KoPubWorldBatang", "Noto Serif KR", serif',
        fontSize: "10.5pt",
        lineHeight: "1.6",
        position: "relative",
      };
    },
    getPdfFooterStyle(pageNumber) {
      const { paddingLeft, paddingRight } = getPagePadding(
        this.pageSizeKey,
        pageNumber
      );
      const footerBottom =
        PAGE_DIMENSIONS[this.pageSizeKey]?.footerBottom || 30;
      return {
        left: `${paddingLeft}px`,
        right: `${paddingRight}px`,
        bottom: `${footerBottom}px`,
      };
    },
    getPdfCommentsStyle(pageNumber) {
      const { paddingLeft, paddingRight } = getPagePadding(
        this.pageSizeKey,
        pageNumber
      );
      const bottom = 55;
      return {
        left: `${paddingLeft}px`,
        right: `${paddingRight}px`,
        bottom: `${bottom}px`,
      };
    },
    normalizeContentToHtml(rawContent) {
      if (!rawContent) return "";
      if (typeof rawContent === "object") {
        if (rawContent.version && Array.isArray(rawContent.blocks)) {
          return renderAstToHtml(rawContent);
        }
        return "";
      }
      if (typeof rawContent !== "string") return "";
      const trimmed = rawContent.trim();
      if (!trimmed) return "";
      if (trimmed.startsWith("{")) {
        try {
          const parsed = JSON.parse(trimmed);
          if (parsed && parsed.version && Array.isArray(parsed.blocks)) {
            return renderAstToHtml(parsed);
          }
        } catch (error) {
          // fall through
        }
      }
      const hasHtml = /<[a-z][\s\S]*>/i.test(trimmed);
      if (hasHtml) {
        return sanitizeHtml(trimmed);
      }
      return renderAstToHtml(parseContentToAst(trimmed));
    },
    processImageUrls(htmlString, { addCrossOrigin = false, preferRelative = false } = {}) {
      if (!htmlString) return htmlString;
      const replaceIskalPath = (match, quote, path) => {
        const normalized = path.startsWith("/") ? path.slice(1) : path;
        if (preferRelative) {
          return `src=${quote}/storage/${normalized}${quote}`;
        }
        if (SERVER_URL) {
          return `src=${quote}${SERVER_URL}/storage/${normalized}${quote}`;
        }
        return match;
      };
      const replaceStorageUrl = (match, quote, path) => {
        const normalized = path.replace(/^\/?storage\//, "");
        if (preferRelative) {
          return `src=${quote}/storage/${normalized}${quote}`;
        }
        if (SERVER_URL) {
          return `src=${quote}${SERVER_URL}/storage/${normalized}${quote}`;
        }
        return match;
      };
      let withAbsolute = htmlString
        .replace(/src=(["])(\/?iskal\/[^"']+)\1/g, replaceIskalPath)
        .replace(/src=(['])(\/?iskal\/[^"']+)\1/g, replaceIskalPath)
        .replace(
          /src=(["'])(https?:\/\/[^"']+\/storage\/[^"']+)\1/g,
          (match, quote, url) => {
            const [, path = ""] = url.split("/storage/");
            return replaceStorageUrl(match, quote, `/storage/${path}`);
          }
        )
        .replace(/src=(["'])(\/?storage\/[^"']+)\1/g, replaceStorageUrl);
      if (!addCrossOrigin) return withAbsolute;
      return withAbsolute.replace(
        /<img(?![^>]*\scrossorigin=)/g,
        '<img crossorigin="anonymous"'
      );
    },
    getCoverCommentsForPaper(paper) {
      const comments = paper?.comments || [];
      if (!comments.length) return [];
      const coverTextParts = [paper?.title, paper?.title_eng];
      (paper?.authors || []).forEach((author) => {
        coverTextParts.push(author?.full_name);
        coverTextParts.push(author?.name_eng);
        coverTextParts.push(author?.organization);
        coverTextParts.push(author?.organization_eng);
      });
      const coverText = coverTextParts.filter(Boolean).join(" ");
      const markerComments = this.collectCommentsByMarkers(
        coverText,
        comments
      );
      const titlePageComments = comments.filter((comment) =>
        this.isTitlePageComment(comment)
      );
      if (!titlePageComments.length) return markerComments;
      const combined = [...markerComments];
      titlePageComments.forEach((comment) => {
        if (!combined.includes(comment)) {
          combined.push(comment);
        }
      });
      return combined;
    },
    collectCommentsByMarkers(text, comments) {
      if (!text || !comments?.length) return [];
      const markerRegex =
        /data-footnote-id="(\d+)"|<sup[^>]*>\^?(\d+)\)?<\/sup>|\^(\d+)/g;
      const result = [];
      const used = new Set();
      let match;
      while ((match = markerRegex.exec(text)) !== null) {
        const footnoteId = match[1] || match[2] || match[3];
        const comment = comments.find((c) => {
          const num = c.superscript?.toString().replace(/\D/g, "");
          return num === footnoteId;
        });
        if (comment && !used.has(comment.idx)) {
          used.add(comment.idx);
          result.push(comment);
        }
      }
      return result;
    },
    isTitlePageComment(comment) {
      if (!comment) return false;
      if (comment.page === "title_page") return true;
      return comment.superscript === "*" && Number(comment.page) === 1;
    },
  },
};
</script>

<style scoped>
.article-preview-wrapper {
  display: flex;
  justify-content: center;
  width: 100%;
}

.preview-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

/* ========== 페이지 공통 ========== */
.paper-page {
  background-color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.page-inner {
  box-sizing: border-box;
}

.page-footer {
  position: absolute;
  bottom: 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: "KoPubWorldDotum", "Noto Sans KR", sans-serif;
  color: #000;
}

.page-footer.page-footer-odd {
  left: 55px;
  right: 35px;
}

.page-footer.page-footer-even {
  left: 35px;
  right: 55px;
}

.page-footer .page-number {
  font-size: 9px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.6);
}

.page-footer .footer-text {
  font-size: 6px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.6);
}

.page-footer.page-footer-odd .page-number {
  order: 2;
}

.page-footer.page-footer-odd .footer-text {
  order: 1;
}

.page-footer.page-footer-even .page-number {
  order: 1;
}

.page-footer.page-footer-even .footer-text {
  order: 2;
}

.page-comments-bottom {
  position: absolute;
  bottom: 55px;
}

.comments-divider {
  width: 60px;
  height: 1px;
  background-color: #000;
  margin-bottom: 6px;
}

.page-comment {
  font-family: "KoPubWorldDotum", "Noto Sans KR", sans-serif;
  font-size: 5.5px;
  line-height: 8px;
  font-weight: 400;
  color: #000;
  text-align: left;
  margin-bottom: 3px;
}

.page-comment sup {
  font-size: 0.8em;
  margin-right: 2px;
}

/* ========== 커버 페이지 ========== */
.cover-title-area {
  background-color: #e8e8e8;
  min-height: 198px;
  padding: 40px 30px;
  text-align: left;
  margin-bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.cover-main-title {
  font-family: "Noto Serif KR", serif;
  font-size: 18pt;
  font-weight: bold;
  line-height: 26pt;
  margin: 0 0 12px 0;
  word-break: keep-all;
  text-align: left;
}

.cover-sub-title {
  font-family: "Noto Serif KR", serif;
  font-size: 12pt;
  font-weight: normal;
  line-height: 1.4;
  margin: 0;
  color: #333;
  text-align: left;
}

.cover-divider {
  height: 1px;
  background-color: #000;
  margin: 0 0 20px 0;
}

.cover-authors-area {
  text-align: left;
  padding-left: 0;
}

.author-item {
  margin-bottom: 8px;
  display: block;
}

.author-name,
.author-name-eng,
.author-org,
.author-org-eng {
  font-size: 10pt;
  color: #333;
  display: inline;
  margin-right: 8px;
}

.author-name {
  font-weight: bold;
  color: #000;
}

/* ========== 초록 페이지 ========== */
.abstract-title {
  font-size: 12pt;
  font-weight: bold;
  margin: 0 0 16px 0;
}

.abstract-content {
  font-size: 10pt;
  line-height: 1.8;
  text-align: justify;
  margin-bottom: 30px;
}

.abstract-content ::v-deep p {
  margin: 0 0 10px 0;
}

.abstract-content ::v-deep p:last-child {
  margin-bottom: 0;
}

.abstract-content ::v-deep em {
  font-style: italic;
}

.keyword-area {
  position: absolute;
  bottom: 60px;
  left: 50px;
  right: 50px;
}

.keyword-label {
  display: inline-block;
  background-color: #666;
  color: white;
  padding: 2px 8px;
  font-size: 9pt;
  margin-right: 8px;
}

.keyword-text {
  font-size: 9pt;
  color: #333;
}
</style>

<style>
/* EditPaperPagesV2 기준 본문 스타일 */
.paper-content {
  font-family: "KoPubWorldBatang", "Noto Serif KR", serif;
}

.paper-content p {
  font-size: 9pt;
  line-height: 15pt;
  text-align: justify;
  text-indent: 1em;
  margin: 6px 0;
  word-break: break-all;
}

.paper-content h2 {
  font-size: 14pt;
  line-height: 20pt;
  font-weight: 700;
  margin: 20px 0 10px;
  padding-bottom: 4px;
  border-bottom: 1.5px solid #333;
}

.paper-content h3 {
  font-size: 11.5pt;
  line-height: 20pt;
  font-weight: 700;
  font-family: "KoPubWorldDotum", "Noto Sans KR", sans-serif;
  margin: 16px 0 8px;
}

.paper-content h4 {
  font-size: 10pt;
  line-height: 18pt;
  font-weight: 600;
  margin: 12px 0 6px;
}

.paper-content h5,
.paper-content h6 {
  font-size: 9pt;
  line-height: 15pt;
  font-weight: 600;
  margin: 10px 0 4px;
}

.paper-content strong {
  font-weight: 700;
}

.paper-content em {
  font-style: italic;
}

.paper-content sup {
  font-size: 0.8em;
  vertical-align: super;
}

.paper-content ul,
.paper-content ol {
  margin: 8px 0 8px 20px;
  font-size: 9pt;
}

.paper-content li {
  margin: 4px 0;
  line-height: 15pt;
}

.paper-content .blockquote,
.paper-content blockquote,
.paper-content .doc-blockquote {
  margin: 12px 0;
  padding: 10px 14px;
  background: #f9fafb;
  border-left: 3px solid #9ca3af;
  font-size: 8pt !important;
  line-height: 13pt;
  color: #4b5563;
  text-indent: 0;
}

.paper-content table {
  width: 100% !important;
  max-width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  border: 1px solid #333;
  font-family: "KoPubWorldDotum", "Noto Sans KR", sans-serif;
  font-size: 7pt;
  line-height: 9.5pt;
  margin: 14px 0;
}

.paper-content table caption {
  padding: 8px;
  caption-side: top;
  text-align: center;
  font-size: 8.5pt;
  line-height: 16.7pt;
  font-weight: 400;
}

.paper-content th,
.paper-content td {
  padding: 2px 4px;
  border: 1px solid #333;
  text-align: center;
  word-break: break-word;
  line-height: 9.5pt;
}

.paper-content thead th {
  background-color: #333;
  color: #fff;
  font-size: 8pt;
  line-height: 10.5pt;
  font-weight: 400;
  text-align: center;
}

.paper-content thead td,
.paper-content tr:first-child th,
.paper-content tr:first-child td {
  text-align: center;
}

.paper-content .page-break {
  display: none;
}

.paper-content .references-title {
  margin-top: 30px;
  font-size: 11pt;
  font-weight: 700;
  margin-bottom: 12px;
}

.paper-content .reference-item {
  margin: 4px 0;
  padding-left: 1.5em;
  text-indent: -1.5em;
  font-size: 8pt;
  line-height: 12pt;
  page-break-inside: avoid;
  break-inside: avoid;
}

.paper-content .figure-container {
  margin: 0px 0;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.paper-content .figure-container .figure-image-wrapper {
  display: inline-block;
  padding: 12px;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
}

.paper-content .figure-container .figure-image-wrapper img {
  max-width: 100%;
  height: auto;
  display: block;
  margin: 0 auto;
  border: none !important;
  border-radius: 0 !important;
  box-shadow: none !important;
}

.paper-content .figure-container > img {
  display: inline-block;
  max-width: calc(100% - 28px);
  height: auto;
  padding: 12px;
  border: 2px dashed #d1d5db !important;
  border-radius: 8px !important;
  box-shadow: none !important;
}

.paper-content .figure-container .figure-placeholder {
  display: inline-block;
  width: 80%;
  max-width: 350px;
  height: 150px;
  background: #f3f4f6;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 12px;
}

.paper-content .figure-container .figure-caption {
  order: -1;
  margin-top: 0;
  margin-bottom: 6px;
  font-family: "KoPubWorldDotum", "Noto Sans KR", sans-serif;
  font-size: 8.5pt;
  line-height: 16.7pt;
  font-weight: 400;
  text-align: center;
}

.paper-content .figure-container.figure-caption-bottom .figure-caption {
  order: 0;
  margin-top: 6px;
  margin-bottom: 0;
}

.paper-content .box-style1,
.paper-content .box-style2,
.paper-content .box-style3,
.paper-content .box-style4 {
  margin: 8px 0;
}

.paper-content .box-style1-title .box-style1-title-text {
  display: inline-flex;
  margin-left: 28px;
  border-radius: 14px 14px 0 0;
  background-color: #e2e3e5;
  padding: 4px 12px;
  font-size: 8.5pt;
  color: #2f2f2f;
}

.paper-content .box-style1-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  border: 1.5px solid #cfd3d7;
  border-radius: 14px;
  padding: 14px;
  width: 100%;
  background-color: #fafafa;
}

.paper-content .box-style1-list-items {
  list-style: none;
  padding: 0;
  margin: 0;
  width: 100%;
}

.paper-content .box-style1-list-item {
  border-bottom: 1px solid #e1e4e8;
  padding: 1px 0;
  margin: 0;
}

.paper-content .box-style1-list-item:last-child {
  border-bottom: none;
}

.paper-content .box-style1-list-item p,
.paper-content .box-style1-list-item div,
.paper-content .box-style1-list-item span {
  margin: 0 !important;
  text-indent: 0 !important;
  line-height: 12pt;
  font-size: 8pt;
}

.paper-content .box-style2-card {
  display: flex;
  flex-direction: column;
  gap: 6px;
  border: 1.5px dotted #9aa0a6;
  border-radius: 10px;
  background-color: #f8f9fb;
  padding: 10px;
  width: 100%;
}

.paper-content .box-style2-card-title {
  display: inline-flex;
  border: 1px solid #9aa0a6;
  border-radius: 10px;
  background-color: #f5f6f8;
  padding: 0 8px;
  width: fit-content;
  color: #4b4f55;
  font-size: 8.5pt;
}

.paper-content .box-style2-card-text {
  color: #2f2f2f;
  line-height: 1.6;
  text-indent: 0;
  font-size: 8.5pt;
}

.paper-content .box-style3 {
  border: 1px solid #d2d6db;
  border-left: 5px solid #8b9096;
  border-radius: 12px;
  background-color: #f6f7f9;
  padding: 10px 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
}

.paper-content .box-style3-header {
  margin-bottom: 4px;
  font-size: 8.5pt;
  color: #2f2f2f;
}

.paper-content .box-style3-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  list-style: none;
  padding: 0;
  margin: 0;
}

.paper-content .box-style3-list p {
  margin: 0;
  font-size: 8.5pt;
}

.paper-content .box-style4 {
  border: 1px solid #d2d5da;
  border-radius: 14px;
  background: linear-gradient(180deg, #f6f7f9 0%, #ffffff 100%);
  padding: 10px 12px;
}

.paper-content .box-style4-head {
  font-size: 8.5pt;
  margin-bottom: 4px;
  color: #2f2f2f;
}

.paper-content .box-style4-body p {
  color: #2f2f2f;
  line-height: 1.6;
  font-size: 8.5pt;
}

.paper-content img {
  border: none !important;
  border-radius: 0 !important;
  padding: 0 !important;
  box-shadow: none !important;
}
</style>
